import { computed, EventEmitter, HostListener, Injectable, signal, WritableSignal } from "@angular/core";
import { FirebaseService } from "../core/firebase/firebase.service";
import { Logger } from "../core/logger.service";
import { TranslateService } from "@ngx-translate/core";
import { ApiService } from "../core/api/api.service";
import { DocDto } from "../dto/doc-dto";
import { AuthenticationService } from "../core/authentication/authentication.service";
import { Subscription } from "rxjs";
import { Router } from "@angular/router";
import { LoaderService } from "../shared/loader/loader.service";
import { BreakpointObserver, Breakpoints, BreakpointState } from "@angular/cdk/layout";

import { ShellEmittersService } from "../shell-emitters.service";
import { MatomoTracker } from "ngx-matomo-client";
import { HomeType } from "./home-type";
import { WorkspaceService } from "../workspace/workspace.service";

// Sezioni del filtro laterale
export enum HomeFilterSection {
  documents = "documents",
  models = "models",
  // trash = "trash",
}

// Tipi di documento
export enum HomeFilterDocType {
  all = "all",
  map = "map",
  editor = "editor",
  pdf = "pdf",
  trash = 'trash',
}

// Tipi di proprietario
export enum HomeFilterOwnership {
  all = "all",
  mine = "mine",
  others = "others",
  public = "public",
}

// Tipi di ordinamento
export enum HomeFilterOrderBy {
  lastOpened = "lastOpened",
  alphabetical = "alphabetical",
}

// Modalita' di visualizzazione
export enum HomeFilterShowAs {
  preview = "preview",
  list = "list",
}

export class HomeFilterDto {
  public expanded: boolean = true;
  public section: HomeFilterSection = HomeFilterSection.documents;
  public docType: Array<string> = [];
  public ownership: Array<string> = [];
  public searchText: string = "";
  public orderBy: HomeFilterOrderBy = HomeFilterOrderBy.lastOpened;
  public showAs: HomeFilterShowAs = HomeFilterShowAs.preview;
  public homeType: HomeType = 'none';
  public showTrashedDocs: boolean = false;
  public tags: Array<string> = [];

}

const logger: Logger = new Logger("HomeFilterService");

@Injectable({
  providedIn: "root",
})
export class HomeFilterService {
  // Gli enum sono pubblicati tramite la classe HomeFilterService:
  // Importare come public filter: HomeFilterService nel constructor
  // In TypeScript: if (filter.section===filter.SECTION.trash)...
  // In HTML: <div *ngIf="filter.section===filter.SECTION.trash">...

  public onSectionChanged = new EventEmitter();
  public onDocTypeChanged = new EventEmitter();
  public onOwnershipChanged = new EventEmitter();
  public onSearchTextChanged = new EventEmitter();
  public onSetOrderByChanged = new EventEmitter();
  public onTagChanged = new EventEmitter();

  public SECTION = HomeFilterSection;
  public DOCTYPE = HomeFilterDocType;
  public OWNERSHIP = HomeFilterOwnership;

  public SHOWAS = HomeFilterShowAs;
  public ORDERBY = HomeFilterOrderBy;

  // Valori del filtro atttuale
  public homeFilterDto: HomeFilterDto = new HomeFilterDto();
  _filtersOpened: WritableSignal<boolean> = signal(false);
  openHomeType = computed(() => this._openHomeType());

  filtersOpened = computed(() => this._filtersOpened());

  private _openHomeType: WritableSignal<HomeType> = signal(undefined);

  public noImageUrl: string = "";

  public allDocs: DocDto[] = new Array<DocDto>();

  private _filteredDocs: WritableSignal<DocDto[]> = signal(new Array<DocDto>());
  public _pageIndex: WritableSignal<number> = signal(0);
  public docsCount = computed(() => this._filteredDocs().length);

  public docs = computed(() => {
    const allFiltered = this._filteredDocs();
    const index = this._pageIndex();
    const start = index * this.docsPageSize;
    const end = start + this.docsPageSize;
    return allFiltered.slice(start, end);
  });

  private _tags: WritableSignal<string[]> = signal(new Array<string>());
  public signalTags = computed(() => this._tags());
  private _section: WritableSignal<HomeFilterSection> = signal(HomeFilterSection.documents);
  public signalSection = computed(() => this._section());
  private _docTypes: WritableSignal<string[]> = signal(new Array<string>());
  public signalDocTypes = computed(() => this._docTypes());
  private _ownership: WritableSignal<string[]> = signal(new Array<string>());
  public signalOwnership = computed(() => this._ownership());

  public emptyTrashEnabled = signal(false);

  panelDocsOpenState = signal(true);
  showTrashedDocs = signal(false);
  compressed = false;



  private currUser = '';
  private getRecentDocsSubscription: Subscription | undefined;

  private FILTER_KEY = 'homeFilter';
  private _FILTER_KEY = 'homeFilter';
  private loadingFilter = false;
  public readonly docsPageSize = 50;

  private refreshRecentSharingSubscription: Subscription | undefined;
  @HostListener("window:storage")
  onStorageChange() {
    this.authenticationService.reloadCredentials();
    if (this.currUser !== this.authenticationService.getUserEmail()) {
      this.currUser = this.authenticationService.getUserEmail();
      this.FILTER_KEY = this.authenticationService.getUserEmail() + '-' + this._FILTER_KEY;
      this.loadFilter(this.authenticationService.getUserEmail());
    }
    if (this.currUser === '') {
      this.clearFilter();
    }

  }

  constructor(
    private router: Router,

    private shellEmittersService: ShellEmittersService,
    private apiService: ApiService,
    private firebaseService: FirebaseService,
    private translateService: TranslateService,
    private authenticationService: AuthenticationService,
    private loaderService: LoaderService,
    private breakpointObserver: BreakpointObserver,
    private tracker: MatomoTracker,
    private workspaceService: WorkspaceService
  ) {
    this.authenticationService.reloadCredentials();
    this.currUser = this.authenticationService.getUserEmail();
    this.FILTER_KEY = this.authenticationService.getUserEmail() + '-' + this._FILTER_KEY;
    this.loadFilter(this.authenticationService.getUserEmail());
    (window as any).filter = this;
    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .subscribe((state: BreakpointState) => {
        this.compressed = state.matches;
      });

    if (this.refreshRecentSharingSubscription) { this.refreshRecentSharingSubscription.unsubscribe(); }
    this.refreshRecentSharingSubscription = this.shellEmittersService.refreshRecentSharing.subscribe(() => {
      this.getDocsFromServer(true).then((data: DocDto[]) => {
        // this.dataSource.set(data);
      });
    });

  }

  saveFilter() {
    if (!this.loadingFilter) {
      const oldSearchText = this.homeFilterDto.searchText;
      this.homeFilterDto.homeType = this.openHomeType();
      this.homeFilterDto.showTrashedDocs = this.showTrashedDocs();

      this.homeFilterDto.searchText = '';

      localStorage.setItem(this.FILTER_KEY, JSON.stringify(this.homeFilterDto));
      this.homeFilterDto.searchText = oldSearchText;

    }
  }
  toggleFiltersOpened() {
    //  this._filtersOpened.update((value) => !this._filtersOpened());
    if (this.filtersOpened() === false) {
      this.setOpenHomeType('filter');

    } else {
      this.setOpenHomeType(undefined);

    }
    this._filtersOpened.update((value) => !this._filtersOpened());
  }

  // setOpenHomeType(openHomeType: HomeType) {
  //   this._openHomeType.update(() => openHomeType);
  // }

  setFiltersOpened(opened: boolean) {
    this._filtersOpened.set(opened);
  }

  loadFilter(useremail: string) {
    this.FILTER_KEY = useremail + '-' + this._FILTER_KEY;
    const filter = localStorage.getItem(this.FILTER_KEY);
    if (filter) {
      const f = JSON.parse(filter);
      this.loadingFilter = true;
      this._openHomeType.update(() => f.homeType);
      //this.setOpenHomeType(f.homeType);
      this.setFiltersOpened(f.expanded);
      this.setTags(f.tags);
      this.setSection(f.section);
      this.setDocType(f.docType, false);
      this.setOwnership(f.ownership, false);
      this.setSearchText(f.searchText);
      this.setOrderBy(f.orderBy);
      this.setShowAs(f.showAs);
      this.showTrashedDocs.update(() => f.showTrashedDocs);
      this.loadingFilter = false;
    }
  }

  //#region impostazione dei filtri
  public getExpanded() {
    return this.homeFilterDto.expanded;
  }
  public setExpanded(expanded: boolean) {
    this.homeFilterDto.expanded = expanded;
    this.saveFilter();
  }

  public getSection() {
    return this.homeFilterDto.section;
  }

  private setShowTrashDocs(value: boolean) {
    this.showTrashedDocs.update(() => value);

  }

  public setOpenHomeType(homeType: HomeType) {
    if (homeType === 'trash') {
      // extraspace chiuso
      // filtro documenti
      this.setShowTrashDocs(this.openHomeType() !== homeType);
      //this.showTrashedDocs.update(() => this.openHomeType() !== homeType);
      const tipo = this.openHomeType() === 'trash' ? undefined : homeType;
      this._openHomeType.update(() => tipo);

    } else {
      this.setShowTrashDocs(false);
      if (homeType === 'filter') {
        // this.setShowTrashDocs(false); //this.showTradDocs.update(() => false);
        const open = homeType === this.openHomeType() ? undefined : homeType;
        this._openHomeType.update(() => open);
        //this.setOpenHomeType(open);        
      } else {
        if (homeType === 'todolist') {
          const open = homeType === this.openHomeType() ? undefined : homeType;
          //  this.showTrashedDocs.update(() => false);

          this._openHomeType.update(() => open);
          //  this.setOpenHomeType(open);

        }
        else {
          this._openHomeType.update(() => homeType);
        }
      }


    }
    this.refresh(true, true);
  }

  public setSection(section: HomeFilterSection) {
    this.homeFilterDto.section = section;
    this._section.update(() => section);
    const value = (section === HomeFilterSection.documents) ? true : false;
    this.openDocsSection(value);
    this.refresh(true, true);
    this.onSectionChanged.emit();
  }

  public getDocType() {
    return this.homeFilterDto.docType;
  }

  public setDocType(docType: Array<string>, trackStat: boolean = true) {
    this.homeFilterDto.docType = docType;
    this._docTypes.update(() => this.homeFilterDto.docType);
    this.refresh(true, true);
    this.onDocTypeChanged.emit();

    //MATOMO event Change home filters
    if (trackStat) {
      try {
        console.log('MATOMO event Change home filters');
        this.tracker.trackEvent('Home', 'Change filter', 'ChangeHomeFilters');
      } catch (e: any) {
        logger.error('matomo error ' + e.message);
      }
    }
  }

  public getTags() {
    return this.homeFilterDto.tags;
  }

  public setTags(tags: Array<string>) {
    if (tags) {
      this.homeFilterDto.tags = tags;
      this._tags.set([...this.homeFilterDto.tags]);
      this.refresh(true, true);
      this.onTagChanged.emit();
      this.shellEmittersService.onUpdateListDocs.emit();
    }

  }

  public getOwnership() {
    return this.homeFilterDto.ownership;
  }

  clearFilter() {
    this.allDocs = [];
    this._filteredDocs.set([]);
  }

  public setOwnership(ownership: Array<string>, trackStat: boolean = true) {
    this.homeFilterDto.ownership = ownership;
    this._ownership.update(() => this.homeFilterDto.ownership);
    this.refresh(true, true);

    this.onOwnershipChanged.emit();

    //MATOMO event Change home filters
    if (trackStat) {
      try {
        console.log("MATOMO event Change home filters");
        this.tracker.trackEvent('Home', 'Change filter', 'ChangeHomeFilters');
      } catch (e: any) {
        logger.error('matomo error ' + e.message);
      }
    }
  }

  public getSearchText() {
    return this.homeFilterDto.searchText;
  }
  public setSearchText(searchText: string) {
    this.homeFilterDto.searchText = searchText;
    this.filterDocs();
    // Non salvare la ricerca
    const old = this.homeFilterDto.searchText;
    this.homeFilterDto.searchText = '';
    this.saveFilter();
    this.homeFilterDto.searchText = old;
    this.onSearchTextChanged.emit();
  }

  public getOrderBy() {
    return this.homeFilterDto.orderBy;
  }
  public setOrderBy(orderBy: HomeFilterOrderBy) {
    this.homeFilterDto.orderBy = orderBy;
    this.onSetOrderByChanged.emit();
    this.refresh(true, true);
  }

  public setPageIndex(pageIndex: number) {
    this._pageIndex.set(pageIndex);
  }

  public getShowAs() {
    return this.homeFilterDto.showAs;
  }
  public setShowAs(showAs: HomeFilterShowAs) {
    this.homeFilterDto.showAs = showAs;
    this.refresh(false, true);
  }

  public refresh(resetPage: boolean = true, saveFilter: boolean = false) {
    if (resetPage) this._pageIndex.set(0);
    if (saveFilter) this.saveFilter();
    this.filterDocs();
  }
  //#endregion

  //#region data
  public getDocsFromServer(showLoader: boolean): Promise<DocDto[]> {
    return new Promise((resolve, reject) => {
      const result = new Array<DocDto>();
      if (!navigator.onLine) {
        logger.error("getDocsFromServer: no connection");
        //is.showError("GATEWAY_TIMEOUT");
      }
      if (this.authenticationService.isAuthenticated()) {
        if (showLoader) this.loaderService.startLoading('generic', 'PLEASE_WAIT');
        this.allDocs = [];
        let recentDoc: DocDto;
        this.firebaseService.getDownloadUri("assets/noimage.png").then((url) => {
          this.noImageUrl = url;
          if (this.getRecentDocsSubscription) {
            this.getRecentDocsSubscription.unsubscribe();
          }
          this.getRecentDocsSubscription = this.apiService.getRecentDocs().subscribe(
            (resp: any) => {
              const viewStringSing = this.translateService.instant("RECENT_VIEW_SING");
              const copyStringSing = this.translateService.instant("RECENT_COPY_SING");
              const viewStringPlur = this.translateService.instant("RECENT_VIEW_PLUR");
              const copyStringPlur = this.translateService.instant("RECENT_COPY_PLUR");
              let num = 0;
              resp.forEach((recent: any) => {
                num++;
                recentDoc = new DocDto(
                  num,
                  recent,
                  viewStringSing,
                  copyStringSing,
                  viewStringPlur,
                  copyStringPlur,

                );
                this.updateDocData(recentDoc, recent.shareData);
                const path = `docs/${recent.id}/preview.jpg`;
                this.getDocPreview(recentDoc, path);
                this.allDocs.push(recentDoc);
              });
              this.refresh(true, false);
              if (showLoader) this.loaderService.stopLoading('generic');
              return resolve(this._filteredDocs());
            },
            (error: any) => {
              if (showLoader) this.loaderService.stopLoading('generic');
              logger.error("getDocsFromServer().subscribe: " + error);
              this.showError(error.code ? error.code : error.status);
            }
          );
        });
      } else {
        if (showLoader) this.loaderService.stopLoading('generic');
        this.router.navigate(["login", "/home"]);
        return reject('not logged in');
      }
    });
  }

  private updateDocData(doc: DocDto, docData: any) {
    if (docData) {
      doc.isShared = docData.isShared ? true : false;
      doc.isPubliclyShared = docData.isPubliclyShared ? true : false;
      doc.isMine = docData.isMine ? true : false;
      doc.readonly = docData.isReadonly ? true : false;
      doc.shareType = docData.shareType ? docData.shareType : "";
      doc.stat_copyShared = docData.stat_copyShared
        ? docData.stat_copyShared
        : 0;
      doc.stat_viewShared = docData.stat_viewShared
        ? docData.stat_viewShared
        : 0;
      doc.updateStrings();
    } else {
      console.log(`No recent data available`);
    }
  }

  getDocPreview(recent: DocDto, path: string) {
    this.firebaseService.getDownloadUri(path).then((url) => {
      recent.previewPublicUrl = url;
    });
  }

  public filterDocs() {
    const docsFiltered = new Array<DocDto>();

    this.allDocs.forEach((doc: DocDto) => {
      const isDeleted =
        doc.deleteDate !== null &&
        doc.deleteDate !== undefined;
      let addDoc = false;
      switch (this.homeFilterDto.section) {
        case this.SECTION.documents:
          if (this.homeFilterDto.ownership.length === 0) {
            if (this.showTrashedDocs()) {
              addDoc = isDeleted;
            } else {
              addDoc = !isDeleted && (this.homeFilterDto.docType.includes(doc.docType) || this.homeFilterDto.docType.length === 0);
            }
          } else {
            if (this.homeFilterDto.ownership.includes("mine")) {
              if (this.showTrashedDocs()) {
                addDoc = isDeleted;
              } else {
                addDoc = !isDeleted && doc.isMine && (this.homeFilterDto.docType.includes(doc.docType) || this.homeFilterDto.docType.length === 0);
              }
            }
            if (!this.showTrashedDocs() && this.homeFilterDto.ownership.includes("others")) {
              addDoc = addDoc || (!isDeleted && !doc.isMine && (this.homeFilterDto.docType.includes(doc.docType) || this.homeFilterDto.docType.length === 0));
            }
          }
          break;
        case this.SECTION.models:
          break;
      }
      if (addDoc) {
        if (this.homeFilterDto.tags?.length > 0) {
          addDoc = false;
          if (doc.tags.length > 0) {
            this.homeFilterDto.tags.forEach(tag => {
              if (doc.tags.includes(tag)) {
                addDoc = true;
              }
            });
          }
        }
        if (addDoc) {
          docsFiltered.push(doc);
        }
      }
    });
    const docFilteredSorted = this.sortRecentDocs(docsFiltered, this.homeFilterDto.orderBy);
    const result = this.applySearchFilter(docFilteredSorted);

    console.log("Result# " + result.length);
    this._filteredDocs.set(result);

    this.emptyTrashEnabled.set((this.showTrashedDocs() && result.length > 0));
    this.shellEmittersService.onUpdateListDocs.emit();
    return result;
  }

  public sortRecentDocs(docs: DocDto[], type: string): DocDto[] {
  const EPOCH_MS = 0;
  const FUTURE_SHIFT_MS = 1000 * 60 * 60 * 24 * 365.25 * 1000;
  const MIN_TIMESTAMP_MS = -2208988800000;

  const safeGetDateTime = (doc: DocDto): number => {
    if (doc.fixDate && doc.fixDate > new Date(EPOCH_MS)) {
      return doc.fixDate.getTime() + FUTURE_SHIFT_MS;
    }
    
    if (doc.lastOpened && !isNaN(doc.lastOpened.getTime())) {
      return doc.lastOpened.getTime();
    }
    
    return MIN_TIMESTAMP_MS;
  };

  const safeGetName = (doc: DocDto): string => 
    doc.name?.toLowerCase() ?? '';

  if (type === this.ORDERBY.alphabetical) {
    // Ordinamento alfabetico puro per nome (case-insensitive)
    return [...docs].sort((a, b) => {
      const nameA = safeGetName(a);
      const nameB = safeGetName(b);
      
      return nameA.localeCompare(nameB); //gestisce caratteri speciali
    });
  }

  // Ordinamento per data (lastOpened): più recenti prima
  return [...docs].sort((a, b) => {
    const timeA = safeGetDateTime(a);
    const timeB = safeGetDateTime(b);
    
    return timeB - timeA; // Decrescente (recenti prima)
  });
}


  applySearchFilter(docs: Array<DocDto>) {
    if (this.homeFilterDto.searchText === "") {
      return docs;
    } else {
      console.log("Apply search filter: " + this.homeFilterDto.searchText);
      const result = new Array<DocDto>();
      docs.forEach((doc: DocDto) => {
        const name = doc.name.toLowerCase();
        if (name.includes(this.homeFilterDto.searchText.toLowerCase())) {
          result.push(doc);
        }
      });
      return result;
    }
  }
  //#endregion

  private showError(errorCode: string) {
    // TODO
    console.error("Error: " + errorCode);
  }

  toggleTrashFiles() {
    const value = this.showTrashedDocs();
    this.setShowTrashDocs(!value);
    this.refresh(true, true)
    this.onSectionChanged.emit();
  }

  openDocsSection(value: boolean = true) {
    this.panelDocsOpenState.update(() => value);
    this.saveFilter();
    this.onSectionChanged.emit();
  }

}
